/**
 * System Achievements API — the platform's own badges.
 *
 * `/admin/...` is rewritten to the Admin API host by the request interceptor, so these reach
 * `api/system-achievements` on admapi. The whole controller is `[Authorize(Roles = "Admin")]`.
 */

import { ApiService } from '../apiService'
import type {
  CreateSystemAchievementRequest,
  SystemAchievementDto,
  SystemAchievementSummaryDto,
  UpdateSystemAchievementRequest,
} from '../types/systemAchievement'

export const systemAchievementsApi = {
  /** Every achievement, hidden ones included — this is the admin list, not a player's. */
  getAll: () =>
    ApiService.get<SystemAchievementSummaryDto[]>('/admin/system-achievements', {
      includeHidden: true,
    }),

  getById: (id: string) =>
    ApiService.get<SystemAchievementDto>(`/admin/system-achievements/${id}`),

  create: (data: CreateSystemAchievementRequest) =>
    ApiService.post<SystemAchievementDto>('/admin/system-achievements', data),

  update: (id: string, data: UpdateSystemAchievementRequest) =>
    ApiService.put<SystemAchievementDto>(`/admin/system-achievements/${id}`, data),

  remove: (id: string) => ApiService.delete<void>(`/admin/system-achievements/${id}`),

  /**
   * Upload (or replace) the achievement's icon. PNG, multipart — the server stores it and sets
   * `IconId`, returning the achievement with a fresh `iconUrl`.
   */
  uploadIcon: (id: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return ApiService.upload<SystemAchievementDto>(`/admin/system-achievements/${id}/icon`, formData)
  },

  /**
   * Recompute everyone's progress for one criterion.
   *
   * Only visits users who already have a progress row for something, so a brand-new criterion
   * backfills for everybody else when they next do something that touches it.
   */
  recalculate: (criteriaType: number) =>
    ApiService.post<void>(`/admin/system-achievements/recalculate/${criteriaType}`),

  /**
   * Re-date already-earned achievements from the activity that earned them.
   *
   * Separate from recalculate, which deliberately leaves a finished badge alone — that is what
   * stops repeated runs from shuffling settled dates, and also why it can never repair one.
   *
   * Defaults to a preview. The report is worth reading before applying: it says what would move
   * and, more usefully, what would not.
   */
  redateEarned: (apply: boolean) =>
    ApiService.post<AchievementRedateReport>(
      `/admin/system-achievements/redate-earned?apply=${apply}`
    ),
}

export interface AchievementRedateGroup {
  criteriaType: string
  examined: number
  moved: number
  unchanged: number
  /** Rows this pass could not date, because the criterion keeps no dated history. */
  unrecoverable: number
  largestCorrectionDays: number
  note?: string | null
}

export interface AchievementRedateReport {
  dryRun: boolean
  groups: AchievementRedateGroup[]
  totalMoved: number
  totalUnchanged: number
}
